import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  InputAdornment,
  CircularProgress,
  Alert,
  Divider,
  Chip
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { RateField } from './RateField';

const isLightPlatformColor = (color = '') => {
  const normalized = String(color).trim().toLowerCase();
  return normalized === '#fff' || normalized === '#ffffff';
};

// Sanitize username input - remove invalid characters
const sanitizeUsername = (value, platform) => {
  let sanitized = value;
  
  // Platform-specific rules
  switch(platform) {
    case 'YouTube':
      // YouTube REQUIRES @ symbol at the start and allows alphanumeric, underscore, period, and hyphen
      // Don't remove @ for YouTube - it's required
      if (!sanitized.startsWith('@')) {
        sanitized = '@' + sanitized;
      }
      // Remove any @ symbols that aren't at the start
      sanitized = '@' + sanitized.slice(1).replace(/@/g, '');
      // Allow only valid characters after the @
      sanitized = sanitized[0] + sanitized.slice(1).replace(/[^a-zA-Z0-9_.-]/g, '');
      break;
      
    case 'Instagram':
    case 'TikTok':
    case 'X':
    case 'Kick':
    case 'Twitch':
      // Remove leading @ symbol for these platforms (we add it as a prefix)
      sanitized = sanitized.replace(/^@+/, '');
      // Allow only alphanumeric, underscore, and period
      sanitized = sanitized.replace(/[^a-zA-Z0-9_.]/g, '');
      break;
      
    default:
      // Remove leading @ and special characters except basic ones
      sanitized = sanitized.replace(/^@+/, '');
      sanitized = sanitized.replace(/[^a-zA-Z0-9_.-]/g, '');
  }
  
  return sanitized;
};

// Validate username format
const validateUsername = (value, platform, isUrl = false) => {
  if (!value || value.trim() === '') {
    return { valid: false, message: 'This field is required' };
  }
  
  // For URLs (like Podcast), just check if it's a valid URL format
  if (isUrl) {
    const urlPattern = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
    if (!urlPattern.test(value)) {
      return { valid: false, message: 'Please enter a valid URL' };
    }
    return { valid: true, message: 'Valid URL' };
  }
  
  // Special validation for YouTube
  if (platform === 'YouTube') {
    if (!value.startsWith('@')) {
      return { 
        valid: false, 
        message: 'YouTube handle must start with @' 
      };
    }
    
    const usernameAfterAt = value.slice(1);
    
    if (usernameAfterAt.length < 1) {
      return { valid: false, message: 'YouTube handle is too short (minimum 1 character after @)' };
    }
    
    if (usernameAfterAt.length > 30) {
      return { valid: false, message: 'YouTube handle is too long (max 30 characters after @)' };
    }
    
    // Check for invalid characters
    if (!/^[a-zA-Z0-9_.-]+$/.test(usernameAfterAt)) {
      return { 
        valid: false, 
        message: 'YouTube handle can only contain letters, numbers, underscores, periods, and hyphens after @' 
      };
    }
    
    return { valid: true, message: 'Valid YouTube handle' };
  }
  
  // Validation for other platforms
  const sanitized = sanitizeUsername(value, platform);
  
  if (sanitized !== value) {
    return { 
      valid: false, 
      message: 'Username contains invalid characters. Only letters, numbers, underscores, and periods are allowed.' 
    };
  }
  
  if (sanitized.length < 1) {
    return { valid: false, message: 'Username is too short.' };
  }
  
  if (sanitized.length > 30) {
    return { valid: false, message: 'Username is too long (max 30 characters).' };
  }
  
  return { valid: true, message: 'Valid username' };
};

const formatNumber = (value) => {
  const number = parseInt(value, 10);
  return isNaN(number) ? "" : number.toString();
};

function PlatformDialog({ 
  open, 
  platform, 
  config, 
  data, 
  saving,
  onClose, 
  onSave,
  onClear
}) {
  const [formData, setFormData] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [touched, setTouched] = useState({});
  
  // Early return if config is not provided - prevents undefined errors during transitions
  if (!config) {
    return null;
  }
  
  // Initialize form data when dialog opens - only when it actually opens
  useEffect(() => {
    if (open && platform) {
      setFormData(data || {});
      setValidationErrors({});
      setTouched({});
    }
  }, [open, platform]); // Remove data from dependencies to prevent re-initialization
  
  // Memoize handlers to prevent recreation
  const handleChange = useCallback((field, value) => {
    // Special handling for follower count
    if (field === 'followerCount') {
      const rawValue = value.replace(/[^0-9]/g, "");
      const formattedValue = formatNumber(rawValue);
      setFormData(prev => ({ ...prev, [field]: formattedValue }));
      return;
    }
    
    // Special handling for link field
    if (field === 'link') {
      const sanitized = config.isUrl ? value : sanitizeUsername(value, platform);
      setFormData(prev => ({ ...prev, [field]: sanitized }));
      
      // Validate on change
      const validation = validateUsername(sanitized, platform, config.isUrl);
      setValidationErrors(prev => ({ ...prev, [field]: validation.valid ? null : validation.message }));
      return;
    }
    
    // For rate fields, just update
    setFormData(prev => ({ ...prev, [field]: value }));
  }, [platform, config.isUrl]);
  
  const handleBlur = useCallback((field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    
    // Validate link field on blur
    if (field === 'link') {
      const validation = validateUsername(formData[field] || '', platform, config.isUrl);
      setValidationErrors(prev => ({ ...prev, [field]: validation.valid ? null : validation.message }));
    }
  }, [formData, platform, config.isUrl]);
  
  // Validate all required fields
  const validateForm = useCallback(() => {
    const errors = {};
    
    // Validate link
    if (!formData.link || formData.link.trim() === '') {
      errors.link = config.isUrl ? 'URL is required' : 'Username is required';
    } else {
      const validation = validateUsername(formData.link, platform, config.isUrl);
      if (!validation.valid) {
        errors.link = validation.message;
      }
    }
    
    // Validate follower count
    if (!formData.followerCount || formData.followerCount === '0' || formData.followerCount === '') {
      errors.followerCount = 'Follower count is required';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, platform, config.isUrl]);
  
  // Handle save - memoize to prevent recreation
  const handleSave = useCallback(() => {
    // Mark all fields as touched
    const allTouched = {};
    config.fields.forEach(field => {
      allTouched[field.key] = true;
    });
    allTouched.link = true;
    setTouched(allTouched);
    
    // Validate
    if (!validateForm()) {
      return;
    }
    
    // Save
    onSave(platform, formData);
  }, [config.fields, validateForm, onSave, platform, formData]);

  const handleClearAll = useCallback(() => {
    const clearedData = config.fields.reduce((acc, field) => {
      acc[field.key] = '';
      return acc;
    }, { link: '', followerCount: '' });

    setTouched({});
    setValidationErrors({});
    setFormData(clearedData);
    onClear(platform, clearedData);
  }, [config.fields, onClear, platform]);
  
  // Check if form is valid - memoize to prevent recalculation
  const hasErrors = useMemo(() => 
    Object.values(validationErrors).some(error => error !== null),
    [validationErrors]
  );
  
  const isFormValid = useMemo(() => 
    formData.link && formData.followerCount && !hasErrors,
    [formData.link, formData.followerCount, hasErrors]
  );

  const visibleErrors = useMemo(
    () => Object.entries(validationErrors).filter(([, error]) => Boolean(error)),
    [validationErrors]
  );
  
  // Determine if we should show the prefix
  // For YouTube, don't show prefix since @ is part of the username
  const shouldShowPrefix = !config.isUrl && platform !== 'YouTube';

  const dialogIconBackground = isLightPlatformColor(config.color) ? '#111111' : config.color;
  const saveButtonBackground = '#111111';
  
  // Get appropriate helper text
  const getLinkHelperText = () => {
    if (touched.link && validationErrors.link) {
      return validationErrors.link;
    }
    
    if (config.isUrl) {
      return 'Enter your full podcast URL';
    }
    
    if (platform === 'YouTube') {
      return 'Enter your YouTube handle (must start with @)';
    }
    
    return `Enter your ${platform.toLowerCase()} username (without @)`;
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        px: 3,
        pt: 2.5,
        pb: 1.5,
        borderBottom: '1px solid #e0e0e0',
        background: 'linear-gradient(180deg, #fafafa 0%, #ffffff 100%)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ 
            width: 48, 
            height: 48, 
            borderRadius: '12px', 
            backgroundColor: dialogIconBackground,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.5rem',
            color: '#ffffff',
            flexShrink: 0,
            '& svg': {
              width: 28,
              height: 28,
            },
            '& span': {
              fontSize: '1.4rem',
              lineHeight: 1,
            }
          }}>
            {config.icon}
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {data?.active ? 'Edit' : 'Add'} {platform}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Configure your {platform.toLowerCase()} profile and rates
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ px: 3, pt: 4.5, pb: 2.5 }}>
        {/* Link/Username Field */}
        <TextField
          fullWidth
          label={
            config.isUrl 
              ? `${platform} Link` 
              : platform === 'YouTube' 
                ? `${platform} Handle` 
                : `${platform} Username`
          }
          value={formData.link || ''}
          onChange={(e) => handleChange('link', e.target.value)}
          onBlur={() => handleBlur('link')}
          error={touched.link && !!validationErrors.link}
          helperText={getLinkHelperText()}
          variant="outlined"
          required
          placeholder={platform === 'YouTube' ? '@yourchannel' : undefined}
          InputLabelProps={{
            shrink: true,
          }}
          sx={{ mb: 2, mt: 1.5 }}
          InputProps={{
            startAdornment: shouldShowPrefix && (
              <InputAdornment position="start">
                <Typography sx={{ color: '#666', fontSize: '0.95rem' }}>
                  {config.prefix}
                </Typography>
              </InputAdornment>
            ),
            endAdornment: touched.link && formData.link && (
              <InputAdornment position="end">
                {validationErrors.link ? (
                  <ErrorOutlineIcon sx={{ color: '#f44336' }} />
                ) : (
                  <CheckCircleOutlineIcon sx={{ color: '#4caf50' }} />
                )}
              </InputAdornment>
            ),
          }}
        />
        
        <Divider sx={{ my: 2 }}>
          <Chip label="Platform Details" size="small" />
        </Divider>
        
        {/* Dynamic Fields */}
        {config.fields.map((field) => {
          if (field.type === 'number') {
            return (
              <TextField
                key={field.key}
                fullWidth
                label={field.label}
                value={formData[field.key] || ''}
                onChange={(e) => handleChange(field.key, e.target.value)}
                onBlur={() => handleBlur(field.key)}
                error={touched[field.key] && !!validationErrors[field.key]}
                helperText={
                  touched[field.key] && validationErrors[field.key]
                    ? validationErrors[field.key]
                    : 'Enter the total number of followers/subscribers'
                }
                variant="outlined"
                required
                placeholder="e.g., 10000"
                sx={{ mb: 2 }}
              />
            );
          }
          
          if (field.type === 'rate') {
            return (
              <RateField
                key={field.key}
                label={field.label}
                fullWidth
                value={formData[field.key] || ''}
                onChange={(value) => handleChange(field.key, value)}
                variant="outlined"
                minValue={field.minValue}
                sx={{ mb: 2 }}
              />
            );
          }
          
          return null;
        })}
        
        {/* Validation Alert */}
        {visibleErrors.length > 0 && Object.keys(touched).length > 0 && (
          <Alert severity="error" sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Please fix the following errors:
            </Typography>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              {visibleErrors.map(([field, error]) =>
                error && <li key={field}>{error}</li>
              )}
            </ul>
          </Alert>
        )}
      </DialogContent>
      
      <DialogActions sx={{ 
        p: 2.5, 
        pt: 2, 
        borderTop: '1px solid #e0e0e0',
        gap: 1
      }}>
        <Button
          onClick={handleClearAll}
          disabled={saving}
          variant="text"
          color="error"
          sx={{ mr: 'auto' }}
        >
          Clear All
        </Button>
        <Button
          onClick={onClose}
          disabled={saving}
          variant="outlined"
          startIcon={<CloseIcon />}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSave}
          disabled={!isFormValid || saving}
          variant="contained"
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          sx={{
            minWidth: 140,
            color: '#ffffff',
            background: `linear-gradient(135deg, ${saveButtonBackground} 0%, #1f2937 100%)`,
            boxShadow: '0 8px 18px rgba(17, 17, 17, 0.18)',
            '&:hover': {
              background: 'linear-gradient(135deg, #000000 0%, #111827 100%)',
              boxShadow: '0 10px 24px rgba(17, 17, 17, 0.24)',
            },
            '&.Mui-disabled': {
              background: 'rgba(0, 0, 0, 0.12)',
              color: 'rgba(0, 0, 0, 0.38)',
              boxShadow: 'none',
            }
          }}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Wrap with React.memo and custom comparison to prevent unnecessary re-renders
export default React.memo(PlatformDialog, (prevProps, nextProps) => {
  // Only re-render if these specific props change
  return (
    prevProps.open === nextProps.open &&
    prevProps.platform === nextProps.platform &&
    prevProps.saving === nextProps.saving &&
    prevProps.data === nextProps.data &&
    prevProps.config === nextProps.config &&
    prevProps.onClear === nextProps.onClear
  );
});
